import { collection, addDoc, onSnapshot } from 'firebase/firestore';
import { m } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import { Input, TextArea, Checkbox } from '../../Components/Form/Form';
import { ContactFormStyle03Schema } from '../../Components/Form/FormSchema';
import GoogleMap from '../../Components/GoogleMap/GoogleMap';
import { db } from '../../config/firebase';
import FooterSection from '../Footer/FooterSection';
import HeaderSection from '../Header/HeaderSection';
import { initializeContactInfo, normalizeContactInfo } from '../../firebase/dbService';
import { getFileURL } from '../../firebase/storageService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

const styles = {
  heroBanner: {
    position: 'relative',
    height: '420px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroImage: {
    position: 'absolute',
    inset: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(12,12,20,0.55) 0%, rgba(12,12,20,0.75) 100%)',
  },
  heroContent: {
    position: 'relative',
    zIndex: 10,
    textAlign: 'center',
    padding: '0 24px',
  },
  heroSubtitle: {
    display: 'inline-block',
    padding: '6px 18px',
    borderRadius: '100px',
    background: 'rgba(212,168,83,0.12)',
    border: '1px solid rgba(212,168,83,0.30)',
    color: '#D4A853',
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  heroTitle: {
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: 'clamp(2rem, 5vw, 3rem)',
    fontWeight: 800,
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.02em',
  },
  section: {
    background: '#FFFBF5',
    padding: '80px 0',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  sectionWhite: {
    background: '#ffffff',
    padding: '80px 0',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  sectionLabel: {
    display: 'inline-block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#D4A853',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    marginBottom: '12px',
  },
  sectionTitle: {
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: '2rem',
    fontWeight: 700,
    color: '#1a1a2e',
    margin: 0,
  },
  infoCard: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '40px 32px',
    height: '100%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    textAlign: 'center',
    cursor: 'default',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  infoCardIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    fontSize: '24px',
  },
  infoCardTitle: {
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: '15px',
    fontWeight: 600,
    color: '#1a1a2e',
    marginBottom: '12px',
  },
  infoCardText: {
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: '14px',
    color: '#6c757d',
    lineHeight: 1.7,
    margin: 0,
  },
  formCard: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '48px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  formInput: {
    fontFamily: "'Inter', system-ui, sans-serif",
    padding: '14px 18px',
    fontSize: '14px',
    border: '1.5px solid #e9ecef',
    borderRadius: '12px',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    color: '#1a1a2e',
    background: '#f8f9fa',
  },
  submitBtn: {
    fontFamily: "'Inter', system-ui, sans-serif",
    padding: '14px 40px',
    fontSize: '15px',
    fontWeight: 600,
    background: '#D4A853',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 16px rgba(212,168,83,0.25)',
  },
  submitBtnDisabled: {
    background: '#dee2e6',
    color: '#adb5bd',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  mapSection: {
    paddingBottom: '0',
    background: '#FFFBF5',
  },
};

const ContactUsClassicPage = (props) => {
  const { t } = useTranslation();
  const form = useRef(null);
  const [contactInfo, setContactInfo] = useState({
    organizationName: "",
    street: "",
    city: "",
    telephone: "",
    fax: "",
    email: ""
  });
  const [pageBackground, setPageBackground] = useState({
    title: '',
    subtitle: '',
    backgroundImageUrl: null
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const contactData = await initializeContactInfo();
        setContactInfo(normalizeContactInfo(contactData));
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    const backgroundRef = collection(db, 'contactUsBackground');
    const unsubscribeBackground = onSnapshot(backgroundRef, async (snapshot) => {
      if (!snapshot.empty) {
        let mostRecentDoc = snapshot.docs[0];
        if (snapshot.docs.length > 1) {
          mostRecentDoc = snapshot.docs.reduce((latest, current) => {
            const latestTime = latest.data().updatedAt?.seconds || 0;
            const currentTime = current.data().updatedAt?.seconds || 0;
            return currentTime > latestTime ? current : latest;
          });
        }
        const data = mostRecentDoc.data();
        let backgroundImageUrl = null;
        if (data.backgroundImage) {
          try {
            backgroundImageUrl = await getFileURL(data.backgroundImage);
          } catch (error) {
            console.error('Error loading background image:', error);
          }
        }
        setPageBackground({
          title: data.title || t('contact.getInTouch'),
          subtitle: data.subtitle || t('contact.contactInfo'),
          backgroundImageUrl
        });
      }
    }, (error) => {
      console.error("Error listening to background changes:", error);
    });

    fetchInitialData();
    return () => unsubscribeBackground();
  }, []);

  const submitContactForm = async (values, { resetForm, setSubmitting }) => {
    try {
      await addDoc(collection(db, "contactSubmissions"), {
        ...values,
        submittedAt: new Date(),
        status: 'new'
      });
      toast.success(t('contact.successMessage'), {
        position: "top-right",
        autoClose: 5000,
      });
      resetForm();
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error(t('contact.errorMessage'), {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ ...props.style, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <HeaderSection />

      {/* Hero Banner */}
      <div style={{ ...styles.heroBanner, background: pageBackground.backgroundImageUrl ? undefined : '#1a1a2e' }}>
        {pageBackground.backgroundImageUrl && (
          <div style={{ ...styles.heroImage, backgroundImage: `url(${pageBackground.backgroundImageUrl})` }} />
        )}
        <div style={styles.heroOverlay} />
        <m.div
          style={styles.heroContent}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div style={styles.heroSubtitle}>{pageBackground.subtitle || t('contact.getInTouch')}</div>
          <h1 style={styles.heroTitle}>{pageBackground.title || t('contact.contactInfo')}</h1>
        </m.div>
      </div>

      {/* Contact Info Cards */}
      <section style={styles.section}>
        <Container>
          <Row className="justify-center" style={{ marginBottom: '48px' }}>
            <Col lg={6} className="text-center">
              <span style={styles.sectionLabel}>{t('contact.getInTouch')}</span>
              <h2 style={styles.sectionTitle}>{t('contact.contactInfo')}</h2>
            </Col>
          </Row>
          <Row>
            <Col lg={4} md={6} style={{ marginBottom: '24px' }}>
              <m.div
                style={styles.infoCard}
                whileHover={{ y: -4, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div style={{ ...styles.infoCardIcon, background: 'rgba(212,168,83,0.1)', color: '#D4A853' }}>
                  <i className="fas fa-building"></i>
                </div>
                <div style={styles.infoCardTitle}>{t('contact.officeAddress')}</div>
                <p style={styles.infoCardText}>
                  {contactInfo.organizationName}<br />
                  {contactInfo.street}<br />
                  {contactInfo.city}
                </p>
              </m.div>
            </Col>
            <Col lg={4} md={6} style={{ marginBottom: '24px' }}>
              <m.div
                style={styles.infoCard}
                whileHover={{ y: -4, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div style={{ ...styles.infoCardIcon, background: 'rgba(123,174,127,0.1)', color: '#7BAE7F' }}>
                  <i className="fas fa-phone-alt"></i>
                </div>
                <div style={styles.infoCardTitle}>{t('contact.phone')}</div>
                <p style={styles.infoCardText}>
                  Tel: {contactInfo.telephone}
                </p>
              </m.div>
            </Col>
            <Col lg={4} md={6} style={{ marginBottom: '24px' }}>
              <m.div
                style={styles.infoCard}
                whileHover={{ y: -4, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div style={{ ...styles.infoCardIcon, background: 'rgba(26,26,46,0.06)', color: '#1a1a2e' }}>
                  <i className="fas fa-envelope"></i>
                </div>
                <div style={styles.infoCardTitle}>{t('contact.email')}</div>
                <p style={styles.infoCardText}>{contactInfo.email}</p>
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form */}
      <section style={styles.sectionWhite}>
        <Container>
          <Row className="justify-center" style={{ marginBottom: '48px' }}>
            <Col lg={6} className="text-center">
              <span style={styles.sectionLabel}>{t('contact.writeToUs')}</span>
              <h2 style={styles.sectionTitle}>{t('contact.sendMessage')}</h2>
            </Col>
          </Row>
          <Row className="justify-center">
            <Col lg={8} md={10}>
              <m.div
                style={styles.formCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Formik
                  initialValues={{
                    name: '',
                    email: '',
                    phone: '',
                    comment: '',
                    terms_condition: false
                  }}
                  validationSchema={ContactFormStyle03Schema}
                  onSubmit={submitContactForm}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      <Row>
                        <Col md={6} style={{ marginBottom: '20px' }}>
                          <Input
                            showErrorMsg={false}
                            className="border-0"
                            style={styles.formInput}
                            id="name"
                            name="name"
                            placeholder={t('contact.name')}
                            type="text"
                          />
                          {touched.name && errors.name && (
                            <span style={{ fontSize: '12px', color: '#e74c3c', marginTop: '6px', display: 'block' }}>{errors.name}</span>
                          )}
                        </Col>
                        <Col md={6} style={{ marginBottom: '20px' }}>
                          <Input
                            showErrorMsg={false}
                            className="border-0"
                            style={styles.formInput}
                            id="email"
                            name="email"
                            placeholder={t('contact.emailPlaceholder')}
                            type="email"
                          />
                          {touched.email && errors.email && (
                            <span style={{ fontSize: '12px', color: '#e74c3c', marginTop: '6px', display: 'block' }}>{errors.email}</span>
                          )}
                        </Col>
                        <Col md={12} style={{ marginBottom: '20px' }}>
                          <Input
                            showErrorMsg={false}
                            className="border-0"
                            style={styles.formInput}
                            id="phone"
                            name="phone"
                            placeholder={t('contact.phonePlaceholder')}
                            type="tel"
                          />
                          {touched.phone && errors.phone && (
                            <span style={{ fontSize: '12px', color: '#e74c3c', marginTop: '6px', display: 'block' }}>{errors.phone}</span>
                          )}
                        </Col>
                        <Col md={12} style={{ marginBottom: '20px' }}>
                          <TextArea
                            showErrorMsg={false}
                            className="border-0"
                            style={{ ...styles.formInput, height: '150px', resize: 'none' }}
                            name="comment"
                            placeholder={t('contact.message')}
                          />
                          {touched.comment && errors.comment && (
                            <span style={{ fontSize: '12px', color: '#e74c3c', marginTop: '6px', display: 'block' }}>{errors.comment}</span>
                          )}
                        </Col>
                        <Col md={12} style={{ marginBottom: '24px' }}>
                          <Checkbox
                            className="inline-block mr-[10px]"
                            name="terms_condition"
                            type="checkbox"
                          >
                            <span style={{ fontSize: '13px', color: '#6c757d', fontFamily: "'Inter', system-ui, sans-serif" }}>
                              {t('contact.consent')}
                            </span>
                          </Checkbox>
                          {touched.terms_condition && errors.terms_condition && (
                            <span style={{ fontSize: '12px', color: '#e74c3c', marginTop: '6px', display: 'block' }}>{errors.terms_condition}</span>
                          )}
                        </Col>
                        <Col className="text-center">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                              ...styles.submitBtn,
                              ...(isSubmitting ? styles.submitBtnDisabled : {}),
                            }}
                            onMouseEnter={e => { if (!isSubmitting) { e.target.style.background = '#c49a47'; e.target.style.transform = 'translateY(-2px)'; } }}
                            onMouseLeave={e => { if (!isSubmitting) { e.target.style.background = '#D4A853'; e.target.style.transform = 'translateY(0)'; } }}
                          >
                            {isSubmitting ? t('contact.sending') : t('contact.send')}
                          </button>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      <FooterSection />
      <ToastContainer />
    </div>
  );
}

export default ContactUsClassicPage;
